<template>
  <v-flex>
    <v-card>
      <v-card-actions>
        <v-layout justify-end>
          <v-flex shrink>
            <v-btn flat icon @click="deleteChart(data.id)">
              <v-icon>close</v-icon>
            </v-btn>
          </v-flex>
        </v-layout>
      </v-card-actions>
      <v-card-text>
        <h2>{{ this.getTitle() }}</h2>
        <p v-if="!this.data.leads" class="no-leads">No leads for this range.</p>
        <canvas :id="data.id"></canvas>
        <p class="unknown-leads">Leads with unknown type: <strong>{{ this.data.unknownLeads }}</strong></p>
      </v-card-text>
    </v-card>
  </v-flex>
</template>

<script>
import Chart from 'chartjs-plugin-funnel';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { mapState, mapActions } from 'vuex';
import { borderColors } from '../../../config/chartColors';
import { DateTime } from 'luxon';

export default {
  name: 'FunnelChart',
  props: ['data'],

  computed: {
    ...mapState('leadOwners', ['owners'])
  },

  data() {
    return {
      chart: null,
      formattedStartDate: null,
      formattedEndDate: null,
      optionConfig: {
        responsive: true,
        sort: 'desc',
        legend: {
          display: false
        },
        title: {
          display: false
        },
        animation: {
          animateScale: true,
          animateRotate: true
        },
        plugins: {
          datalabels: {
            color: '#000000',
            formatter: this.labelFormatter,
            borderWidth: 1,
            borderColor: '#cccccc',
            backgroundColor: '#ffffff',
            padding: 6,
            opacity: 0.8,
            font: {
              weight: '500',
              lineHeight: 1.4,
              size: 13
            }
          }
        },
        tooltips: {
          enabled: false
        }
      },
      labelConfig: ['Leads', 'Orders', 'Closes'],
      backgroundColorConfig: [borderColors[0], borderColors[1], borderColors[2]],
      typeConfig: 'funnel'
    };
  },

  methods: {
    ...mapActions('salesFunnel', ['deleteChart']),

    getOwner() {
      return this.owners.find(owner => owner.id === this.data.ownerId).name;
    },

    labelFormatter(value, context) {
      let label = `${this.labelConfig[context.dataIndex]}: ${value}`;
      if (context.dataIndex === 0) {
        label += `\nLeads to Orders: ${((this.data.orders / this.data.leads) * 100).toFixed(2)}%`;
        label += `\nLeads to Closes: ${((this.data.closes / this.data.leads) * 100).toFixed(2)}%`;
      } else if (context.dataIndex === 1) {
        label += `\nOrders to Closes: ${((this.data.closes / this.data.orders) * 100).toFixed(2)}%`;
      }
      return label;
    },

    buildChart() {
      if (this.data.leads) {
        const chartConfig = this.formatChart();
        const ctx = document.getElementById(this.data.id).getContext('2d');
        this.chart = new Chart(ctx, chartConfig);
      }
    },

    formatChart() {
      const chartConfig = {
        type: this.typeConfig,
        plugins: [ChartDataLabels],
        data: {
          datasets: [{
            data: [this.data.leads, this.data.orders, this.data.closes],
            backgroundColor: this.backgroundColorConfig,
            hoverBackgroundColor: this.hoverBackgroundColorConfig
          }],
          labels: this.labelConfig
        },
        options: this.optionConfig
      };
      return chartConfig;
    },

    getLeadType() {
      return this.data.opportunityType ? `${this.data.opportunityType.charAt(0).toUpperCase() + this.data.opportunityType.slice(1)}` : 'All';
    },

    getTitle() {
      return `${this.getLeadType()} leads from ${this.formattedStartDate} - ${this.formattedEndDate} ${this.data.ownerId ? 'for ' + this.getOwner() : ''}`;
    },

    formatDates() {
      this.formattedStartDate = DateTime.fromFormat(this.data.startDate, 'yyyy-LL-dd').toFormat('LL/dd/yy');
      this.formattedEndDate = DateTime.fromFormat(this.data.endDate, 'yyyy-LL-dd').toFormat('LL/dd/yy');
    }
  },

  mounted() {
    this.formatDates();
    this.buildChart();
  }
};
</script>

<style lang="scss" scoped>
  .capitalize {
    text-transform: capitalize;
  }

  canvas {
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
  }

  h2 {
    text-align: center;
    margin-bottom: 12px;
  }

  .no-leads {
    text-align: center;
    margin-top: 12px;
  }

  .card__text {
    padding-top: 0px;
  }

  .unknown-leads {
    margin-top: 16px;
    text-align: center;
  }
</style>



// WEBPACK FOOTER //
// src/components/reports/leads/FunnelChart.vue